// ─────────────────────────────────────────────────────────────────────────────
// Canonical campaign engagement-status taxonomy — SINGLE SOURCE OF TRUTH.
//
// Before this module CARR had three competing status vocabularies with
// conflicting color maps (EngagementStatusBadge 19-entry map, CampaignHub
// StatusStrip 13 chips, CampaignDetail 14-status list). The same status
// rendered in different colors on different pages (e.g. bounced was red in the
// badge but orange in the Hub strip). Every campaign surface now imports from
// here so a given status always has ONE label and ONE color.
//
// Engagement status answers "where is this contact in the outreach funnel?".
// It is a distinct dimension from `outcome` (post-reply disposition) — see
// OutcomeBadge. Raw values emitted by EmailBison / HeyReach webhooks and the
// outreach_messages lifecycle are normalized into this canonical set via
// STATUS_ALIASES. See docs/runbooks/campaign_status_taxonomy.md.
// ─────────────────────────────────────────────────────────────────────────────

/** Canonical engagement statuses, ordered as the outreach funnel reads. */
export const CANONICAL_ENGAGEMENT_STATUSES = [
  "planned",
  "drafted",
  "queued",
  "loaded",
  "sent",
  "connection_sent",
  "connected",
  // opened/clicked are retained for backend/historical safety but are NOT
  // surfaced in the UI — CARR doesn't run open/click tracking. They are
  // excluded from channelStatuses() and the legend; the view collapses them to
  // 'sent' so no contact should actually carry these statuses.
  "opened",
  "clicked",
  "replied",
  "out_of_office",
  "job_changed",
  "handoff_complete",
  "meeting_booked",
  "bounced",
  "unsubscribed",
] as const;

export type CanonicalStatus = (typeof CANONICAL_ENGAGEMENT_STATUSES)[number];

/**
 * Statuses that must NOT appear in any campaign-performance surface (table,
 * funnel tiles, filter chips, Hub status strips, badges).
 *
 * - `skipped`: a PRE-FILTER. The lead was excluded before entering the campaign;
 *   showing it pollutes funnel counts. Skipped contacts remain accessible only
 *   via the dedicated "Skipped (N)" audit dialog (useCampaignSkippedContacts).
 * - `error` / `archived` / `no_email`: webhook/lifecycle plumbing states, not
 *   user-actionable engagement states.
 */
export const HIDDEN_STATUSES: ReadonlySet<string> = new Set([
  "skipped",
  "error",
  "archived",
  "no_email",
]);

/**
 * Raw → canonical alias map. Covers EmailBison/HeyReach webhook values and the
 * outreach_messages.status lifecycle so every surface can normalize before
 * rendering. Values already in CANONICAL_ENGAGEMENT_STATUSES need no entry.
 */
export const STATUS_ALIASES: Record<string, string> = {
  // Pre-send authoring states collapse to the canonical funnel head
  pending: "planned",
  not_started: "planned",
  // Queue lifecycle synonyms
  ready: "queued",
  ready_to_send: "queued",
  pending_send: "queued",
  waiting_for_connection: "queued",
  waiting_for_conversation: "queued",
  // Send synonyms
  pushed: "sent",
  message_sent: "sent",
  // Connection synonyms
  accepted: "connected",
  pre_connected: "connected",
  // Reply-category synonyms (EmailBison categorizeReply → outreach_messages.reply_category)
  no_longer_at_company: "job_changed",
  // Failure/plumbing → hidden buckets
  failed: "error",
};

/**
 * One Tailwind class string per canonical status (light + dark). Hidden
 * statuses get a muted style for the rare surface that still renders them
 * (e.g. the Skipped audit dialog).
 */
export const STATUS_COLOR: Record<string, string> = {
  planned: "bg-slate-100 text-slate-600 dark:bg-slate-800/50 dark:text-slate-300",
  drafted: "bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300",
  queued: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300",
  loaded: "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300",
  sent: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300",
  connection_sent: "bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300",
  connected: "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300",
  opened: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-300",
  clicked: "bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300",
  replied: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300",
  out_of_office: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300",
  job_changed: "bg-stone-200 text-stone-700 dark:bg-stone-800 dark:text-stone-300",
  handoff_complete: "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/50 dark:text-fuchsia-300",
  meeting_booked: "bg-emerald-200 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200",
  bounced: "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300",
  unsubscribed: "bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300",
  // Hidden / plumbing
  skipped: "bg-slate-100 text-slate-500 dark:bg-slate-900/50 dark:text-slate-400",
  error: "bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300",
};

const FALLBACK_COLOR = "bg-muted text-muted-foreground";

/** Human-readable label overrides. Anything not listed is Title-Cased. */
export const STATUS_LABEL: Record<string, string> = {
  connection_sent: "Conn. Sent",
  meeting_booked: "Meeting",
  unsubscribed: "Unsub",
  out_of_office: "Out of Office",
  job_changed: "Job Changed",
  handoff_complete: "Handoff Complete",
};

/**
 * Per-channel ordered status subset — drives which chips/columns are worth
 * showing so each surface reads cleanly at a glance.
 */
export function channelStatuses(channel: "email" | "linkedin"): CanonicalStatus[] {
  if (channel === "linkedin") {
    return [
      "planned",
      "queued",
      "loaded",
      "connection_sent",
      "connected",
      "replied",
      "meeting_booked",
    ];
  }
  // email — opened/clicked intentionally omitted (CARR doesn't track them).
  return [
    "planned",
    "drafted",
    "queued",
    "loaded",
    "sent",
    "replied",
    "out_of_office",
    "job_changed",
    "handoff_complete",
    "meeting_booked",
    "bounced",
    "unsubscribed",
  ];
}

/** Normalize any raw status to its canonical form (lowercased, alias-resolved). */
export function normalizeStatus(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const s = raw.toLowerCase().trim();
  return STATUS_ALIASES[s] ?? s;
}

/** True when a (normalized) status should be hidden from performance surfaces. */
export function isHiddenStatus(raw: string | null | undefined): boolean {
  const s = normalizeStatus(raw);
  return s != null && HIDDEN_STATUSES.has(s);
}

/** Tailwind color classes for a (raw or canonical) status. */
export function statusColor(raw: string | null | undefined): string {
  const s = normalizeStatus(raw) ?? "";
  return STATUS_COLOR[s] ?? FALLBACK_COLOR;
}

/** Display label for a (raw or canonical) status. */
export function statusLabel(raw: string | null | undefined): string {
  const s = normalizeStatus(raw) ?? "pending";
  return STATUS_LABEL[s] ?? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
